import { ReactElement, useEffect, useState } from 'react';
import { Control, Path, useForm } from 'react-hook-form';
import {
	Box,
	Button,
	FormControlLabel,
	IconButton,
	Paper,
	Radio,
	RadioGroup
} from '@material-ui/core';
import Delete from '@material-ui/icons/Delete';
import ArrowUpward from '@material-ui/icons/ArrowUpward';
import ArrowDownward from '@material-ui/icons/ArrowDownward';
import Info from '@material-ui/icons/Info';
import { equals, remove } from 'ramda';
import { Asset, ILatLng, LegendItem } from '../../../redux';
import { Body1, ErrorBoundary, Header4, TextInputField } from '../../atoms';
import { MapViewer, MapViewerControls } from '..';
import { convertLatLngInterfaceToLatLng, extractPrefix } from '../../../utils';
import {
	ConfirmationDialog,
	FormValidationErrors,
	IConfirmationDialogProps,
	RadioGroupField
} from '../../molecules';
import { useTranslation } from 'react-i18next';

export interface IAssetManagerFormProps {
	asset: Asset;
	saveAsset: (asset: Asset) => void;
	removeFromCache: (casperId: string) => void;
	removeAllFromCache: () => void;
}

export function AssetManagerForm(props: IAssetManagerFormProps): ReactElement {
	const { asset, saveAsset, removeFromCache, removeAllFromCache } = props;
	const {
		control,
		formState: { errors, submitCount },
		getValues,
		watch,
		reset,
		handleSubmit
	} = useForm<Asset>({
		defaultValues: { ...asset, legendItems: [], legendAssetId: '' },
		mode: 'onSubmit',
		reValidateMode: 'onChange'
	});

	const { t } = useTranslation('contentManager');

	const [clearErrorFlag, setClearErrorFlag] = useState<boolean>(false);
	const [saveClicked, setSaveClicked] = useState<boolean>(false);
	const [confirmationDialogProps, setConfirmationDialogProps] =
		useState<IConfirmationDialogProps>({
			onCancel: closeConfirmationDialog,
			onConfirm: () => {},
			open: false,
			title: t('assets.delete.deleteCacheItem'),
			confirmText: t('assets.delete.delete'),
			bodyText: t('assets.delete.confirmDeleteCacheItem')
		});

	const assetPreview = watch();
	const useProjection: boolean = assetPreview.useProjection;

	useEffect(() => {
		if (asset && !equals(getValues(), asset)) {
			reset(asset);
		}
		if (saveClicked) {
			setClearErrorFlag(true);
			setSaveClicked(false);
		}
	}, [asset]);

	function closeConfirmationDialog(): void {
		setConfirmationDialogProps({
			...confirmationDialogProps,
			open: false
		});
	}

	function openConfirmDialog(
		fn: () => void,
		title: string,
		bodyText: string
	): void {
		setConfirmationDialogProps({
			...confirmationDialogProps,
			onConfirm: fn,
			open: true,
			title,
			bodyText
		});
	}

	function handleRemoveFromCache(casperId: string): void {
		openConfirmDialog(
			() => removeFromCache(casperId),
			t('assets.delete.deleteCacheItem'),
			t('assets.delete.confirmDeleteCacheItem')
		);
	}

	function handleRemoveAllFromCache(): void {
		openConfirmDialog(
			() => removeAllFromCache(),
			t('assets.delete.deleteAllCacheItems'),
			t('assets.delete.confirmDeleteAllCacheItems')
		);
	}

	function onSubmit(data: Asset): void {
		saveAsset(data);
		setSaveClicked(true);
	}

	function onResetValues(legendType: string): void {
		reset({
			...getValues(),
			legendAssetId:
				legendType === 'layers' ? asset.legendAssetId : undefined,
			legendItems: legendType === 'image' ? asset.legendItems : undefined
		});
	}

	function setLegendLayers(legendItems: LegendItem[]): void {
		reset({ ...getValues(), legendItems });
	}

	function onUseprojectionChanged(value: string): void {
		const castedValue: boolean = value === 'false' ? false : true;
		reset({
			...assetPreview,
			useProjection: castedValue
		});
	}

	return (
		<form
			className="map-asset-manager-form"
			onSubmit={handleSubmit(onSubmit)}
		>
			<TextInputField
				label={t('assets.formFields.assetName')}
				name="assetName"
				control={control}
				required={t('assets.formFields.requiredAssetName')}
				fullWidth
			/>
			<TextInputField
				label={t('assets.formFields.casperAssetId')}
				name="assetId"
				control={control}
				required={t('assets.formFields.requiredCasperAssetId')}
				fullWidth
			/>
			<Box className="projection-container">
				<RadioGroupField
					name="useProjection"
					control={control}
					values={[
						{
							value: true,
							label: t('assets.formFields.useProjection')
						},
						{
							value: false,
							label: t('assets.formFields.noProjection')
						}
					]}
					onChange={onUseprojectionChanged}
					valueTransformer={(value: string) => !!value}
				/>
				<Box className="corner-fields">
					<AngleField
						control={control}
						number={1}
						longitudeName={
							useProjection ? 'projectedCorner.lng' : 'corner.lng'
						}
						latitudeName={
							useProjection ? 'projectedCorner.lat' : 'corner.lat'
						}
					/>

					<AngleField
						control={control}
						number={2}
						longitudeName={
							useProjection
								? 'projectedOppositeCorner.lng'
								: 'oppositeCorner.lng'
						}
						latitudeName={
							useProjection
								? 'projectedOppositeCorner.lat'
								: 'oppositeCorner.lat'
						}
					/>

					{useProjection && (
						<TextInputField
							name="projection"
							control={control}
							label={t('assets.formFields.projectionText')}
							multiline
							rows={10}
							required={t('assets.formFields.requiredProjection')}
							fullWidth
						/>
					)}
				</Box>
			</Box>
			<LegendField
				layers={assetPreview.legendItems}
				control={control}
				resetValues={onResetValues}
				setLegendLayers={setLegendLayers}
			/>
			<Box display="flex" justifyContent="space-between">
				<Button type="submit" color="primary" variant="contained">
					{t('save')}
				</Button>
				<Box>
					<Button
						color="primary"
						variant="outlined"
						onClick={() =>
							handleRemoveFromCache(assetPreview.assetId)
						}
					>
						{t('assets.formFields.removeAssetCache')}
					</Button>
					<Button
						color="primary"
						variant="outlined"
						className="clear-cache-button"
						onClick={handleRemoveAllFromCache}
					>
						{t('assets.formFields.removeAllAssetsCache')}
					</Button>
				</Box>
			</Box>
			<FormValidationErrors errors={errors} submitCount={submitCount} />
			{assetPreview && !assetPreview.id && (
				<Paper className="preview-info">
					<Info />
					<Body1>{t('assets.formFields.previewAsset')}</Body1>
				</Paper>
			)}
			{assetPreview && assetPreview.id && (
				<AssetMapPreview
					asset={assetPreview}
					shouldClearError={clearErrorFlag}
					setShouldClearError={setClearErrorFlag}
				/>
			)}
			<ConfirmationDialog {...confirmationDialogProps} />
		</form>
	);
}

function AngleField({
	longitudeName,
	latitudeName,
	control,
	number
}: {
	longitudeName: Path<Asset>;
	latitudeName: Path<Asset>;
	control: Control<Asset>;
	number: Number;
}) {
	const { t } = useTranslation('contentManager');

	return (
		<>
			<Header4>{t('assets.formFields.angleNumber', { number })}</Header4>
			<Box className="corner">
				<TextInputField
					name={longitudeName}
					control={control}
					type="number"
					label={t('assets.formFields.longitude')}
					required={t(
						'assets.formFields.requiredLongitudeWithNumber',
						{ number }
					)}
					fullWidth
				/>
				<TextInputField
					name={latitudeName}
					control={control}
					type="number"
					label={t('assets.formFields.latitude')}
					required={t(
						'assets.formFields.requiredLatitudeWithNumber',
						{ number }
					)}
					fullWidth
				/>
			</Box>
		</>
	);
}

function LegendField({
	control,
	layers,
	resetValues,
	setLegendLayers
}: {
	control: Control<Asset>;
	layers?: LegendItem[];
	resetValues: (legendType: string) => void;
	setLegendLayers: (legendItems: LegendItem[]) => void;
}): ReactElement {
	const [legendType, setLegendType] = useState<string>('layers');
	const { t } = useTranslation('contentManager');

	function handleChange(
		_: React.ChangeEvent<HTMLInputElement>,
		value: string
	): void {
		setLegendType(value);
		resetValues(value);
	}

	function addLayer(): void {
		const newItem: LegendItem = { key: '', value: '' };
		const newLayers: LegendItem[] = layers
			? [...layers, newItem]
			: [newItem];

		setLegendLayers(newLayers);
	}

	function moveUp(index: number): () => void {
		return () => {
			if (!layers) return;
			const newLayers: LegendItem[] = [...layers];

			const temp: LegendItem = layers[index - 1];
			newLayers[index - 1] = newLayers[index];
			newLayers[index] = temp;

			setLegendLayers(newLayers);
		};
	}

	function moveDown(index: number): () => void {
		return () => {
			if (!layers) return;
			const newLayers: LegendItem[] = [...layers];

			const temp: LegendItem = layers[index + 1];
			newLayers[index + 1] = newLayers[index];
			newLayers[index] = temp;

			setLegendLayers(newLayers);
		};
	}

	function deleteRow(index: number): () => void {
		return () => {
			if (!layers) return;

			setLegendLayers(remove(index, 1, [...layers]));
		};
	}

	return (
		<Box className="legend-manager">
			<RadioGroup
				aria-label="Legenda soort"
				value={legendType}
				onChange={handleChange}
			>
				<FormControlLabel
					value="image"
					control={<Radio color="primary" />}
					label={t('assets.formFields.legend.image')}
				/>
				<FormControlLabel
					value="layers"
					control={<Radio color="primary" />}
					label={t('assets.formFields.legend.layers')}
				/>
			</RadioGroup>
			{legendType === 'image' && (
				<TextInputField
					name="legendAssetId"
					control={control}
					label={t('assets.formFields.legend.casperId')}
					fullWidth
				/>
			)}
			{legendType === 'layers' && (
				<Box className="layers">
					{layers &&
						layers.map((_, index: number) => (
							<Box className="layer" key={index}>
								<TextInputField
									name={
										`legendItems.${index}.key` as Path<Asset>
									}
									control={control}
									label={t('assets.formFields.legend.key')}
									fullWidth
								/>
								<TextInputField
									name={
										`legendItems.${index}.value` as Path<Asset>
									}
									control={control}
									label={t('assets.formFields.legend.value')}
									fullWidth
								/>
								<Box className="action-buttons">
									{index > 0 && (
										<IconButton onClick={moveUp(index)}>
											<ArrowUpward />
										</IconButton>
									)}
									{index < layers.length - 1 && (
										<IconButton onClick={moveDown(index)}>
											<ArrowDownward />
										</IconButton>
									)}
									<IconButton onClick={deleteRow(index)}>
										<Delete />
									</IconButton>
								</Box>
							</Box>
						))}
					<Button
						color="primary"
						variant="contained"
						onClick={addLayer}
					>
						{t('assets.formFields.legend.addLayer')}
					</Button>
				</Box>
			)}
		</Box>
	);
}

interface IAssetMapPreviewProps {
	asset: Asset;
	shouldClearError: boolean;
	setShouldClearError: (shouldClear: boolean) => void;
}

function AssetMapPreview(props: IAssetMapPreviewProps): ReactElement {
	const { shouldClearError, asset, setShouldClearError } = props;
	const {
		assetId,
		useProjection,
		corner,
		oppositeCorner,
		projectedCorner,
		projectedOppositeCorner,
		legendItems
	} = asset;

	function clearErrorFlag(): void {
		setShouldClearError(false);
	}

	return (
		<ErrorBoundary clear={shouldClearError} resetClear={clearErrorFlag}>
			<MapViewer
				mapId="asset-id"
				casperId={assetId}
				corner={convertLatLngInterfaceToLatLng(
					(useProjection ? projectedCorner : corner) as ILatLng
				)}
				oppositeCorner={convertLatLngInterfaceToLatLng(
					(useProjection
						? projectedOppositeCorner
						: oppositeCorner) as ILatLng
				)}
				projection={useProjection ? asset.projection : undefined}
			>
				<MapViewerControls
					mapId="asset-id"
					prefix={extractPrefix(assetId)}
					corner={convertLatLngInterfaceToLatLng(
						(useProjection ? projectedCorner : corner) as ILatLng
					)}
					oppositeCorner={convertLatLngInterfaceToLatLng(
						(useProjection
							? projectedOppositeCorner
							: oppositeCorner) as ILatLng
					)}
					attachmentCount={0}
					options={{
						mapActions: false,
						disableLegendDrag: true
					}}
					legendItems={legendItems}
				/>
			</MapViewer>
		</ErrorBoundary>
	);
}
